/**
 * Fiche joueur (profil + stats saison) servie par /data/players.json (refresh
 * QUOTIDIEN côté serveur — voir server/playerData.mjs). Chargée une seule fois et
 * mise en cache ; un échec réseau laisse la fiche muette, jamais d'écran cassé
 * (règle 8). Consommée par src/ui/playerStatsTip.ts au survol du mini-terrain.
 */
export interface PlayerInfo {
  readonly name: string | null;
  readonly age: number | null;
  readonly photo: string | null;
  readonly nationality: string | null;
  readonly pos: string | null;
  readonly number?: number | null;
  readonly teamCode?: string | null;
  readonly apps: number;
  readonly goals: number;
  readonly assists: number;
  readonly rating: number | null;
}

/** Un joueur de la sélection (profil + son id, pour la fiche pays). */
export interface SquadPlayer extends PlayerInfo {
  readonly id: number;
}

/** « Dernier match » d'un joueur (note du match le plus récent, + adversaire). */
export interface PlayerForm {
  readonly matchId: number;
  readonly oppCode: string | null;
  readonly rating: number | null;
  readonly minutes: number | null;
  readonly goals: number;
  readonly assists: number;
}

/** Une ligne d'historique : la perf d'un joueur sur UN match du tournoi. */
export interface PlayerMatchRow {
  readonly matchId: number;
  readonly teamCode: string | null;
  /** Adversaire résolu côté serveur (fiable même en phase finale). */
  readonly oppCode?: string | null;
  readonly rating: number | null;
  readonly minutes: number | null;
  readonly goals: number;
  readonly assists: number;
  readonly shots: number | null;
  readonly shotsOn: number | null;
  readonly passes: number | null;
  readonly keyPasses: number | null;
  readonly passAcc: number | null;
  readonly tackles: number | null;
  readonly duelsTotal: number | null;
  readonly duelsWon: number | null;
  readonly dribblesAtt: number | null;
  readonly dribbles: number | null;
}

/** Historique match par match d'un joueur (fiche dédiée). */
export interface PlayerHistory {
  readonly id: number;
  readonly matches: readonly PlayerMatchRow[];
}

let cache: Record<string, PlayerInfo> | null = null;
let pending: Promise<Record<string, PlayerInfo> | null> | null = null;
let formCache: Record<string, PlayerForm> | null = null;
let formPending: Promise<Record<string, PlayerForm> | null> | null = null;
const histCache = new Map<number, PlayerHistory | null>();
const histPending = new Map<number, Promise<PlayerHistory | null>>();

function load(): Promise<Record<string, PlayerInfo> | null> {
  if (cache) return Promise.resolve(cache);
  if (pending) return pending;
  pending = fetch("data/players.json")
    .then((r) => (r.ok ? (r.json() as Promise<{ players?: Record<string, PlayerInfo> }>) : null))
    .then((doc) => {
      cache = doc?.players ?? null;
      return cache;
    })
    .catch(() => null)
    .finally(() => {
      pending = null;
    });
  return pending;
}

function loadForm(): Promise<Record<string, PlayerForm> | null> {
  if (formCache) return Promise.resolve(formCache);
  if (formPending) return formPending;
  formPending = fetch("data/player-form.json")
    .then((r) => (r.ok ? (r.json() as Promise<Record<string, PlayerForm>>) : null))
    .then((doc) => {
      formCache = doc ?? null;
      return formCache;
    })
    .catch(() => null)
    .finally(() => {
      formPending = null;
    });
  return formPending;
}

/** Fiche d'un joueur par son id API-Football, ou null si absente/indisponible. */
export async function fetchPlayer(id: number): Promise<PlayerInfo | null> {
  const players = await load();
  return players?.[String(id)] ?? null;
}

/** « Dernier match » d'un joueur (note récente), ou null. */
export async function fetchPlayerForm(id: number): Promise<PlayerForm | null> {
  const form = await loadForm();
  return form?.[String(id)] ?? null;
}

/** Historique match par match d'un joueur (mis en cache par id), ou null. */
export async function fetchPlayerHistory(id: number): Promise<PlayerHistory | null> {
  if (histCache.has(id)) return histCache.get(id) ?? null;
  const inflight = histPending.get(id);
  if (inflight) return inflight;
  const p = fetch(`data/player-history/${id}.json`)
    .then((r) => (r.ok ? (r.json() as Promise<PlayerHistory>) : null))
    .then((doc) => {
      const val = doc && Array.isArray(doc.matches) ? doc : null;
      histCache.set(id, val);
      return val;
    })
    .catch(() => null)
    .finally(() => {
      histPending.delete(id);
    });
  histPending.set(id, p);
  return p;
}

/** Sélection complète d'un pays (tous les joueurs de team_code), ou liste vide. */
export async function fetchSquad(teamCode: string): Promise<SquadPlayer[]> {
  const players = await load();
  if (!players) return [];
  const out: SquadPlayer[] = [];
  for (const [id, p] of Object.entries(players)) {
    if (p.teamCode === teamCode) out.push({ ...p, id: Number(id) });
  }
  return out;
}

/** Précharge les fichiers pour que le 1er survol soit instantané. */
export function preloadPlayers(): void {
  void load();
  void loadForm();
}
